import { Group, Input, Stack, Text } from '@mantine/core'
import { UseFormReturnType } from '@mantine/form'
import { IconEyeOff, IconLock } from '@tabler/icons-react'
import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import { LabelMultiSelect } from '@/components/labels/LabelMultiSelect/LabelMultiSelect'
import { CheckboxCard } from '@/components/ui-shared/CheckboxCard/CheckboxCard'
import { CompactSlider } from '@/components/ui-shared/CompactSlider/CompactSlider'
import { LabelWithHint } from '@/components/ui-shared/LabelWithHint/LabelWithHint'
import { PlatformAlert } from '@/components/ui-shared/PlatformAlert/PlatformAlert'
import { Label } from '@/types/label'
import { FormValues } from '../DeploymentSettings'
import { SettingsCard } from '../SettingsCard'

type BlurSettingsProps = {
  form: UseFormReturnType<FormValues>
  labels: Label[]
  showStandardBlurOptions: boolean
}

export const BlurSettings = ({
  form,
  labels,
  showStandardBlurOptions
}: BlurSettingsProps) => {
  const labelOptions = useMemo(
    () =>
      labels.map((label) => ({
        labelId: label.id,
        name: label.name,
        color: label.color
      })),
    [labels]
  )

  const { blur_face, blur_lp, blur_label_ids } = form.getValues().blurOptions
  const isBlurOn = blur_face || blur_lp || (blur_label_ids ?? []).length > 0

  return (
    <SettingsCard
      title={<FormattedMessage id="deployments.blurSettings.title" />}
      description={
        <FormattedMessage id="deployments.blurSettings.description" />
      }
      contentMaxWidth={480}
    >
      <Stack gap="xl" pb="md">
        {showStandardBlurOptions && isBlurOn && (
          <PlatformAlert variant="warning" compact>
            <FormattedMessage id="deployments.blurSettings.ownContainer" />
          </PlatformAlert>
        )}

        {showStandardBlurOptions && (
          <Stack gap="xs">
            <Group gap={6} align="center">
              <IconLock size={16} />
              <Text size="sm" fw={500}>
                <FormattedMessage id="deployments.blurSettings.standardPrivacy.title" />
              </Text>
            </Group>

            <CheckboxCard
              defaultBg="white"
              checked={form.getValues().blurOptions.blur_face}
              title={
                <FormattedMessage id="deployments.blurSettings.face.label" />
              }
              description={
                <FormattedMessage id="deployments.blurSettings.face.description" />
              }
              onClick={() =>
                form.setFieldValue(
                  'blurOptions.blur_face',
                  !form.getValues().blurOptions.blur_face
                )
              }
            />

            <CheckboxCard
              defaultBg="white"
              checked={form.getValues().blurOptions.blur_lp}
              title={
                <FormattedMessage id="deployments.blurSettings.lp.label" />
              }
              description={
                <FormattedMessage id="deployments.blurSettings.lp.description" />
              }
              onClick={() =>
                form.setFieldValue(
                  'blurOptions.blur_lp',
                  !form.getValues().blurOptions.blur_lp
                )
              }
            />
          </Stack>
        )}

        {labelOptions.length > 0 && (
          <Stack gap="xs">
            <Group gap={6} align="center">
              <IconEyeOff size={16} />
              <Text size="sm" fw={500}>
                <FormattedMessage id="deployments.blurSettings.customClasses.title" />
              </Text>
            </Group>

            <Text size="xs" c="dimmed">
              <FormattedMessage id="deployments.blurSettings.customClasses.description" />
            </Text>

            <LabelMultiSelect
              placeholder={
                <FormattedMessage id="deployments.blurSettings.customClasses.placeholder" />
              }
              options={labelOptions}
              values={form.getValues().blurOptions.blur_label_ids ?? []}
              onChange={(values) =>
                form.setFieldValue('blurOptions.blur_label_ids', values)
              }
            />
          </Stack>
        )}

        <Input.Wrapper
          label={
            <LabelWithHint
              label={
                <FormattedMessage id="deployments.blurSettings.transparency.label" />
              }
              hint={
                <FormattedMessage id="deployments.blurSettings.transparency.tooltip" />
              }
            />
          }
        >
          <CompactSlider
            mt="xl"
            min={0}
            max={1}
            step={0.1}
            marks={[
              { value: 0, label: '0' },
              { value: 1, label: '1' }
            ]}
            {...form.getInputProps('blurOptions.blur_transparency')}
          />
        </Input.Wrapper>
      </Stack>
    </SettingsCard>
  )
}
